import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { utils } from '@grammarly/badger-ui'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { rhfToFormData } from '../../form/rhf-helpers'
import {
  useApiService,
  useBrowserService,
  useExperimentService,
  useUserService,
  useFreeLimitToTrialExperiment,
} from '../../../lib/hooks'
import { TrialModalContent } from '@components/ui-lib/modal/free-user-trial-modal'
import { gnarSpec, gnarClient, EventProps } from '../../../lib/tracking/gnar'
import { PageContext } from '../../../contexts/page-context'
import { transformToObject, transformToString } from '../../../lib/utils/error'
import { aiDetectorQuestionStructure, AIDetectorState, aiDetectorYOffset, focusTimeout } from './utils'
import { maxCountSubmitAnonymous, useUpdateAIDetectorCountSubmit } from './local-storage-state'
import { useShouldTriggerUsageWall } from './use-should-trigger-usage-wall'
import { logError, LogErrorMetrics } from '../../../lib/tracking/felog'
import { feMetrics } from '../../../lib/tracking/metrics'
import * as CmsMetrics from '../../../lib/tracking/metrics/metrics'
import {
  getExpOptimalUsageLimit,
  isToolsOptimalUsageLimitTestTreatment,
  UrlBasedUsageTracker,
} from '../../../lib/utils/tool-usage'

interface AIDetectorContextValue {
  slug: string
  isMobileOrTablet: boolean
  appElementForModal: string | HTMLElement
  isAnonymous: boolean
  methods: ReturnType<typeof useForm<Record<string, string>>>
  aiDetectorState: AIDetectorState
  setAIDetectorState: (val: AIDetectorState) => void
  aiPercent: number | null
  setAiPercent: (val: number | null) => void
  outputRef: React.RefObject<HTMLDivElement>
  onClear: () => void
  onSubmit: SubmitHandler<Record<string, string>>
  showUsageWall: boolean
  setShowUsageWall: (val: boolean) => void
  showTrialModal: boolean
  setShowTrialModal: (val: boolean) => void
  trialModalContent: TrialModalContent
  trackModalShow?: () => void
  onFormChangeCapture: (event: React.ChangeEvent<HTMLFormElement>) => void
  onFormClickCapture: (event: React.MouseEvent<HTMLFormElement>) => void
  gnarTrackFn: (trackEventProps: EventProps) => Promise<void>
  onChange: () => void
  pageId: string
}

const [BaseProvider, useAIDetector] = utils.createContext<AIDetectorContextValue>({
  name: 'AIDetector',
})

export interface AIDetectorProviderProps {
  children: React.ReactNode

  /** Set for using modals */
  appElementForModal: string | HTMLElement

  /** Passes whether the user is anonymous */
  isAnonymous: boolean
}

export const AIDetectorProvider = (props: AIDetectorProviderProps) => {
  const { children, appElementForModal, isAnonymous } = props
  const { pageId, slug } = useContext(PageContext)
  const { isMobileOrTablet } = useBrowserService()
  const { capi } = useApiService()
  const user = useUserService().currentUser
  const { peekToolsOptimalUsageLimitAssignment, getToolsOptimalUsageLimitAssignment } = useExperimentService()

  // Usage tracking - anonymous users for signup wall, free users for trial modal
  const peekedToolsOptimalUsageLimitAssignment = peekToolsOptimalUsageLimitAssignment(isAnonymous, slug)
  const isOptimalUsageLimitTestTreatment = isToolsOptimalUsageLimitTestTreatment(peekedToolsOptimalUsageLimitAssignment)

  const {
    usageTracker: freeTrialUsageTracker,
    showTrialModal,
    setShowTrialModal,
    trialModalContent,
    trackModalShow,
  } = useFreeLimitToTrialExperiment()

  const usageTracker = useMemo(() => {
    if (isOptimalUsageLimitTestTreatment) {
      const usageLimit = getExpOptimalUsageLimit(peekedToolsOptimalUsageLimitAssignment, maxCountSubmitAnonymous)
      return new UrlBasedUsageTracker(usageLimit, false, undefined, user)
    }
    return freeTrialUsageTracker
  }, [isOptimalUsageLimitTestTreatment, peekedToolsOptimalUsageLimitAssignment, user, freeTrialUsageTracker])

  const updateAIDetectorCountSubmit = useUpdateAIDetectorCountSubmit(usageTracker)
  const shouldTriggerUsageWall = useShouldTriggerUsageWall(usageTracker)
  const [showUsageWall, setShowUsageWall] = useState(false)

  const triggerUsageWall = useCallback(() => {
    setShowUsageWall(true)
  }, [])

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      [aiDetectorQuestionStructure.fields.name]: '',
    },
  })
  const { reset, formState, setFocus } = methods
  const { isDirty, isSubmitSuccessful } = formState

  const [aiDetectorState, setAIDetectorState] = useState<AIDetectorState>('initial')

  const [aiPercent, setAiPercent] = useState<number | null>(null)
  const outputRef = useRef<HTMLDivElement>(null)

  const hasInitialChangeRef = useRef(false)

  const gnarTrackFn = useCallback(
    (trackEventProps: EventProps) => {
      return gnarClient.track({ ...trackEventProps, pageId, pageSlug: slug })
    },
    [pageId, slug]
  )

  const onFormChangeCapture = (event: React.ChangeEvent<HTMLFormElement>) => {
    getToolsOptimalUsageLimitAssignment(isAnonymous, slug)
    if (shouldTriggerUsageWall) {
      triggerUsageWall()
      event.stopPropagation()
    }
  }

  const onFormClickCapture = (event: React.MouseEvent<HTMLFormElement>) => {
    getToolsOptimalUsageLimitAssignment(isAnonymous, slug)
    if (shouldTriggerUsageWall) {
      const formInputTagNames = new Set(['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'])
      const { target } = event
      const isTargetFormInput =
        formInputTagNames.has((target as HTMLElement).tagName) ||
        formInputTagNames.has((target as HTMLElement).parentElement?.tagName ?? '')
      if (isTargetFormInput) {
        event.stopPropagation()
      }
    }
  }

  const onChange = () => {
    if (!hasInitialChangeRef.current) {
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Input)
        .setContext({
          toolPage: CmsMetrics.ToolType.AiDetector,
        })
        .increment()
      hasInitialChangeRef.current = true
    }
  }

  const onSubmit: SubmitHandler<Record<string, string>> = useCallback(
    async (data, e) => {
      e?.preventDefault()
      gnarSpec.aiDetectorFormSubmit(pageId, slug)

      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Submit)
        .setContext({
          toolPage: CmsMetrics.ToolType.AiDetector,
        })
        .increment()

      if (shouldTriggerUsageWall) {
        triggerUsageWall()
        return
      }

      if (usageTracker?.checkAndTriggerLimitReached()) {
        return
      }

      const formData = rhfToFormData(data)
      const formValue = formData.get(aiDetectorQuestionStructure.fields.name) || ''
      const userText = typeof formValue === 'string' ? formValue.trim() : String(formValue)

      setAIDetectorState('loading')

      try {
        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Attempt)
          .setContext({
            toolPage: CmsMetrics.ToolType.AiDetector,
          })
          .increment()

        const response = await capi.detectAI(userText)

        setAiPercent(response.score)

        if (isMobileOrTablet && outputRef.current) {
          const y = outputRef.current.getBoundingClientRect().top + window.scrollY + aiDetectorYOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
        setAIDetectorState('done')
        updateAIDetectorCountSubmit()

        gnarSpec.aiDetectorGenerateSuccess(pageId, slug)
        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Success)
          .setContext({
            toolPage: CmsMetrics.ToolType.AiDetector,
          })
          .increment()
      } catch (err) {
        console.error('Error checking text', err)

        setAIDetectorState('error')
        reset(undefined, { keepValues: true, keepSubmitCount: true })

        gnarSpec.aiDetectorGenerateFailure(pageId, slug, transformToString(err as object))

        logError('Error generating AI Detector response', LogErrorMetrics.AIDetectorError, {
          error: transformToObject(err as Error),
        })
        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Failure)
          .setContext({
            toolPage: CmsMetrics.ToolType.AiDetector,
          })
          .increment()
      }
    },
    [
      pageId,
      slug,
      shouldTriggerUsageWall,
      triggerUsageWall,
      usageTracker,
      capi,
      isMobileOrTablet,
      updateAIDetectorCountSubmit,
      reset,
    ]
  )

  const onClear = useCallback(() => {
    gnarSpec.aiDetectorCtaClick('clear', pageId, slug, 'textArea')
    setAIDetectorState('initial')
    setAiPercent(null)
    reset()
    setTimeout(() => {
      setFocus(aiDetectorQuestionStructure.fields.name)
    }, focusTimeout)
  }, [pageId, slug, reset, setFocus])

  useEffect(() => {
    if (isDirty && isSubmitSuccessful && aiDetectorState === 'error') {
      setAIDetectorState('done')
    }
  }, [isDirty, isSubmitSuccessful, aiDetectorState, setAIDetectorState])

  return (
    <BaseProvider
      value={{
        slug,
        isMobileOrTablet,
        appElementForModal,
        isAnonymous,
        methods,
        aiDetectorState,
        setAIDetectorState,
        aiPercent,
        setAiPercent,
        outputRef,
        onClear,
        onSubmit,
        showUsageWall,
        setShowUsageWall,
        showTrialModal,
        setShowTrialModal,
        trialModalContent,
        trackModalShow,
        onFormChangeCapture,
        onFormClickCapture,
        gnarTrackFn,
        onChange,
        pageId,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </BaseProvider>
  )
}

export { useAIDetector }
